'use client'

import { useRef, useEffect } from 'react'
import { cn } from '@/lib/tailwind'
import { ENTRY_PRESENTATION } from '@/entries'
import type { EntryType } from '@/document'

const MAX_TITLE_HEIGHT = 96 // ~4 lines at text-lg leading-snug

interface FormTitleSectionProps {
  title: string
  onTitleChange: (value: string) => void
  isAnalyzing: boolean
  hasShownTitle: boolean
  isLoading: boolean
  effectiveType?: EntryType | null
  onTypeToggle?: () => void
}

/**
 * Title input with auto-resize.
 * Entry type badge appears inline after the title text (left-aligned, not right-pinned).
 */
export function FormTitleSection({
  title,
  onTitleChange,
  isAnalyzing,
  hasShownTitle,
  isLoading,
  effectiveType,
  onTypeToggle,
}: FormTitleSectionProps) {
  const titleRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto'
      titleRef.current.style.height =
        Math.min(titleRef.current.scrollHeight, MAX_TITLE_HEIGHT) + 'px'
    }
  }, [title])

  const presentation = effectiveType ? ENTRY_PRESENTATION[effectiveType] : null
  const TypeIcon = presentation?.icon ?? null
  const typeLabel = presentation?.label ?? null
  const typeColor = presentation?.text ?? null

  return (
    <div className="relative min-w-0">
      <textarea
        id="title"
        ref={titleRef}
        placeholder="Title (optional)"
        value={title}
        onChange={(e) => {
          onTitleChange(e.target.value)
          e.target.style.height = 'auto'
          e.target.style.height =
            Math.min(e.target.scrollHeight, MAX_TITLE_HEIGHT) + 'px'
        }}
        disabled={isLoading}
        rows={1}
        className={cn(
          'placeholder:text-muted-foreground/50 w-full resize-none overflow-auto border-0 bg-transparent px-0 text-base leading-snug font-normal transition-opacity duration-200 focus-visible:ring-0 focus-visible:outline-none',
          isAnalyzing && hasShownTitle && 'opacity-0',
        )}
      />
      {isAnalyzing && hasShownTitle && (
        <div
          className="bg-muted/50 absolute inset-y-0 left-0 h-7 w-2/3 animate-pulse rounded"
          role="status"
          aria-label="Loading title suggestion"
        />
      )}

      {TypeIcon && typeLabel && typeColor && (
        <button
          type="button"
          onClick={onTypeToggle}
          className="mt-0.5 mb-2 flex items-center gap-1 rounded transition-opacity hover:opacity-70"
          aria-label={`Change type (currently ${effectiveType})`}
        >
          <TypeIcon className={cn('h-3 w-3', typeColor)} weight="fill" />
          <span
            className={cn(
              'text-[10px] font-medium tracking-wide uppercase',
              typeColor,
            )}
          >
            {typeLabel}
          </span>
        </button>
      )}
    </div>
  )
}
